import { assetClassFor, type KnownAssetClass } from '../portfolio/asset-class.js';
import type {
  LightyearParseResult,
  LightyearTransaction,
  RowError,
  SkippedRow,
} from './lightyear.js';
import type { Currency } from '../types/enums.js';

/**
 * What an import would do, worked out before anything is written.
 *
 * The preview and the write must agree, so they are the same object: the page
 * shows this, and the mutation walks the same `toInsert` list. Anything that
 * decides "new or already here" in the mutation instead would be a second
 * implementation of the question the user was just shown an answer to.
 */

/** An instrument the rows reference that the database does not have yet. */
export interface PlannedInstrument {
  isin: string;
  ticker: string;
  currency: Currency;
  assetClass: KnownAssetClass;
}

/** A row the parser read fine but the ledger cannot accept. */
export interface PlanProblem {
  externalId: string;
  message: string;
}

export interface ImportPlan {
  parserVersion: number;
  /** False when the file's date order could not be settled from the data. */
  dateOrderCertain: boolean;
  /** Rows to write, deduplicated on external id. */
  toInsert: LightyearTransaction[];
  /** Rows this account already has under the same external id. */
  alreadyPresent: LightyearTransaction[];
  newInstruments: PlannedInstrument[];
  skipped: SkippedRow[];
  /** Malformed rows, from the parser. */
  errors: RowError[];
  /** Well-formed rows the schema would reject. */
  problems: PlanProblem[];
}

export interface PlanInput {
  parsed: LightyearParseResult;
  /** `external_id` values already stored against the target account. */
  existingExternalIds: ReadonlySet<string>;
  /** ISINs that already have an instrument row for this user. */
  knownIsins: ReadonlySet<string>;
}

/**
 * Lightyear says nothing about what kind of instrument a row is, so
 * everything starts as equity and `assetClassFor` corrects the ISINs we know
 * better — the money-market funds it parks cash in. See O-11.
 */
const DEFAULT_ASSET_CLASS: KnownAssetClass = 'equity';

export function planLightyearImport({
  parsed,
  existingExternalIds,
  knownIsins,
}: PlanInput): ImportPlan {
  const toInsert: LightyearTransaction[] = [];
  const alreadyPresent: LightyearTransaction[] = [];
  const problems: PlanProblem[] = [];
  const seen = new Set<string>();

  for (const transaction of parsed.transactions) {
    // `investment_transactions_quantity_required`: a trade with no instrument
    // is rejected by the database, so catch it here where it can be explained.
    if ((transaction.type === 'buy' || transaction.type === 'sell') && transaction.isin === null) {
      problems.push({
        externalId: transaction.externalId,
        message: `${transaction.type === 'buy' ? 'Vétel' : 'Eladás'} ISIN nélkül: nem lehet instrumentumhoz kötni.`,
      });
      continue;
    }

    if (existingExternalIds.has(transaction.externalId)) {
      alreadyPresent.push(transaction);
      continue;
    }

    // One upsert cannot touch the same row twice; Postgres rejects the whole
    // statement rather than the row, so the duplicate has to go before it.
    if (seen.has(transaction.externalId)) {
      problems.push({
        externalId: transaction.externalId,
        message: 'Ez az azonosító kétszer szerepel a fájlban; a második sor kimarad.',
      });
      continue;
    }

    seen.add(transaction.externalId);
    toInsert.push(transaction);
  }

  return {
    parserVersion: parsed.parserVersion,
    dateOrderCertain: parsed.dateOrderCertain,
    toInsert,
    alreadyPresent,
    newInstruments: planInstruments(toInsert, knownIsins),
    skipped: parsed.skipped,
    errors: parsed.errors,
    problems,
  };
}

/**
 * Instruments the rows about to be written need.
 *
 * Keyed by ISIN, never by ticker: Lightyear calls IE00BYZK4552 `2B76` and
 * IBKR calls it `RBOT`. By ticker that is two instruments and the portfolio
 * shows one position twice; by ISIN it is one exposure at two brokers, which
 * is the whole point of the app.
 */
function planInstruments(
  transactions: readonly LightyearTransaction[],
  knownIsins: ReadonlySet<string>,
): PlannedInstrument[] {
  const planned = new Map<string, PlannedInstrument>();

  for (const transaction of transactions) {
    const { isin, ticker } = transaction;
    if (isin === null || ticker === null) continue;
    if (knownIsins.has(isin) || planned.has(isin)) continue;

    planned.set(isin, {
      isin,
      ticker,
      currency: transaction.currency,
      assetClass: assetClassFor(isin, DEFAULT_ASSET_CLASS),
    });
  }

  return [...planned.values()];
}

/** True when running the import would change nothing. */
export function isNoop(plan: ImportPlan): boolean {
  return plan.toInsert.length === 0 && plan.newInstruments.length === 0;
}
